import { skipToken, useQuery } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdTickets } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Ticket } from '~hooks/useTickets/models';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';

interface UseDiscardedTicketsQueryProps {
  companyId?: string;
  cursor?: string;
  direction?: PaginationLink;
  fromDate?: string;
  toDate?: string;
}

/**
 * Soft-deleted tickets for the Settings > Deleted Records page.
 *
 * This index names its date params `filter[from_date]` / `filter[to_date]` where orders
 * and jobs use `filter[start_date]` / `filter[end_date]`; the wrong pair is accepted
 * silently and simply does not filter, so these prop names mirror the API's.
 *
 * It also declares them as `format: date`, so they MUST be `YYYY-MM-DD` — a timestamp is
 * rejected with a 400 — and compares `created_at` against `to_date` at midnight in the
 * company timezone, making to_date exclusive.
 *
 * The range covers `tickets.created_at`, not deletion time: `discarded_at` is not exposed
 * on Ticket-Read.
 */
export default function useDiscardedTicketsQuery({
  companyId,
  cursor,
  direction,
  fromDate,
  toDate,
}: UseDiscardedTicketsQueryProps) {
  const query = useQuery({
    queryKey: ['discardedTickets', companyId, direction, cursor, fromDate, toDate],
    queryFn: companyId
      ? async ({ signal }) => {
          try {
            const response = await getV1CompaniesCompanyIdTickets({
              path: { 'company-id': companyId },
              query: {
                'filter[discarded]': true,
                'page[limit]': MAX_API_PAGE_SIZE,
                ...(direction && cursor ? { [`page[${direction}]`]: cursor } : {}),
                ...(fromDate ? { 'filter[from_date]': fromDate } : {}),
                ...(toDate ? { 'filter[to_date]': toDate } : {}),
              },
              signal,
            });

            return {
              pagination: extractPagination(response),
              tickets: response.data.data.map(Ticket.parse),
            };
          } catch (error) {
            connection.handleRequestError(
              error,
              t('error_messages.tickets.failed_to_fetch'),
            );
            throw error;
          }
        }
      : skipToken,
  });

  return {
    isFetching: query.isFetching,
    pagination: query.data?.pagination,
    tickets: query.data?.tickets ?? [],
  };
}
